import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminAPI, { getTileTypes, createTile, updateTile, deleteTile } from './AdminAPI';
import { TILE_KIND_BASE, TILE_KIND_DETAIL } from '../GameWorld/tiles';
import {
  DEFAULT_DETAIL_RENDER_SCALE as DEFAULT_RENDER_SCALE_TILES,
  DETAIL_RENDER_DIMENSION_MAX as MAX_RENDER_DIMENSION_TILES,
  DETAIL_RENDER_SCALE_MAX as MAX_RENDER_SCALE_TILES,
  DETAIL_RENDER_SCALE_MIN as MIN_RENDER_SCALE_TILES,
  normalizeRenderDimensionTiles,
  normalizeRenderScaleTiles,
  normalizeTileData,
  normalizeTileTags,
} from '../GameWorld/tileUtils';
import './Admin_Tiles.css';
import useImageCache from '../../hooks/useImageCache';
import { MEDIA_VARIANTS } from '../../media';
import { buildTileImagePrompt } from './tileImagePrompt';
import AdminColorDot from './AdminColorDot';

const NEW_TILE_ID = '__new';

function mapTileResponse(tile) {
  const normalized = normalizeTileData(tile, (tile?.type || tile?.Type) === TILE_KIND_DETAIL ? TILE_KIND_DETAIL : TILE_KIND_BASE);
  if (!normalized) {
    return null;
  }

  const name = tile?.name || tile?.Name || normalized.label || normalized.key;
  return {
    ...normalized,
    id: tile?.id || tile?.Id || normalized.id,
    name,
    label: name,
    visualDescription: tile?.visualDescription || tile?.VisualDescription || '',
    renderScaleTiles: normalized.type === TILE_KIND_DETAIL
      ? normalizeRenderScaleTiles(tile?.renderScaleTiles ?? tile?.RenderScaleTiles)
      : DEFAULT_RENDER_SCALE_TILES,
    renderWidthTiles: normalized.renderWidthTiles,
    renderHeightTiles: normalized.renderHeightTiles,
  };
}

function createDraftFromTile(tile, defaultType = TILE_KIND_BASE) {
  if (!tile) {
    return {
      id: null,
      name: '',
      key: '',
      type: defaultType,
      category: '',
      tags: '',
      visualDescription: '',
      fallbackColor: defaultType === TILE_KIND_BASE ? '#6b7280' : '#94a3b8',
      imageId: null,
      walkable: defaultType === TILE_KIND_BASE,
      renderScaleTiles: DEFAULT_RENDER_SCALE_TILES,
      renderWidthTiles: DEFAULT_RENDER_SCALE_TILES,
      renderHeightTiles: DEFAULT_RENDER_SCALE_TILES,
    };
  }

  const type = tile.type || defaultType;
  return {
    id: tile.id || null,
    name: tile.name || tile.label || '',
    key: tile.key || '',
    type,
    category: tile.category || '',
    tags: normalizeTileTags(tile.tags).join(', '),
    visualDescription: tile.visualDescription || '',
    fallbackColor: tile.fallbackColor || '#6b7280',
    imageId: tile.imageId || null,
    walkable: type === TILE_KIND_BASE ? tile.walkable : true,
    renderScaleTiles: type === TILE_KIND_DETAIL
      ? normalizeRenderScaleTiles(tile.renderScaleTiles)
      : DEFAULT_RENDER_SCALE_TILES,
    renderWidthTiles: type === TILE_KIND_DETAIL
      ? normalizeRenderDimensionTiles(
        tile.renderWidthTiles,
        tile.renderScaleTiles,
      )
      : DEFAULT_RENDER_SCALE_TILES,
    renderHeightTiles: type === TILE_KIND_DETAIL
      ? normalizeRenderDimensionTiles(
        tile.renderHeightTiles,
        tile.renderScaleTiles,
      )
      : DEFAULT_RENDER_SCALE_TILES,
  };
}

function validateTileDraft(draft) {
  if (!draft) return 'Nothing to save';
  if (!draft.name || !draft.name.trim()) return 'Name is required';
  if (!draft.key || !draft.key.trim()) return 'Key is required';
  if (draft.type !== TILE_KIND_BASE && draft.type !== TILE_KIND_DETAIL) return 'Invalid tile type';
  if (draft.type === TILE_KIND_DETAIL) {
    for (const [label, value] of [
      ['width', draft.renderWidthTiles],
      ['height', draft.renderHeightTiles],
    ]) {
      const dimension = Number(value);
      if (
        !Number.isFinite(dimension)
        || dimension < MIN_RENDER_SCALE_TILES
        || dimension > MAX_RENDER_DIMENSION_TILES
      ) {
        return `Render ${label} must be between ${MIN_RENDER_SCALE_TILES} and ${MAX_RENDER_DIMENSION_TILES} tiles`;
      }
    }
  }
  return null;
}

function AdminTiles() {
  const [tiles, setTiles] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [selectedTileId, setSelectedTileId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const fetchImage = useCallback((imageId, variant) => AdminAPI.fetchImage(imageId, variant), []);
  const getImageUrl = useImageCache(fetchImage);

  const loadTiles = useCallback(async (preferredId = null) => {
    try {
      const list = await getTileTypes();
      if (!Array.isArray(list)) {
        setTiles([]);
        setSelectedTileId(null);
        setDraft(null);
        return;
      }

      const normalized = list
        .map(mapTileResponse)
        .filter(Boolean)
        .sort((a, b) => {
          if (a.type === b.type) {
            return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
          }
          return a.type.localeCompare(b.type);
        });

      setTiles(normalized);

      if (!normalized.length) {
        setSelectedTileId(null);
        setDraft(null);
        return;
      }

      const targetId = preferredId;
      const match = normalized.find((tile) => tile.id === targetId);
      if (match) {
        setSelectedTileId(match.id);
        setDraft(createDraftFromTile(match, match.type));
      } else {
        const first = normalized[0];
        setSelectedTileId(first.id);
        setDraft(createDraftFromTile(first, first.type));
      }
    } catch (error) {
      console.error('Failed to load tiles', error);
    }
  }, []);

  useEffect(() => {
    loadTiles();
  }, [loadTiles]);

  const typeFilteredTiles = useMemo(() => {
    if (!Array.isArray(tiles)) return [];
    if (filterType === 'all') return tiles;
    return tiles.filter((tile) => tile.type === filterType);
  }, [tiles, filterType]);

  const tagOptions = useMemo(() => {
    const counts = new Map();
    typeFilteredTiles.forEach((tile) => {
      normalizeTileTags(tile.tags).forEach((tag) => {
        counts.set(tag, (counts.get(tag) || 0) + 1);
      });
    });

    return Array.from(counts.entries())
      .sort(([left], [right]) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
      .map(([tag, count]) => ({ tag, count }));
  }, [typeFilteredTiles]);

  const activeFilterTag = filterTag === 'all' || tagOptions.some(({ tag }) => tag === filterTag)
    ? filterTag
    : 'all';

  useEffect(() => {
    if (filterTag !== activeFilterTag) {
      setFilterTag(activeFilterTag);
    }
  }, [activeFilterTag, filterTag]);

  const filteredTiles = useMemo(() => {
    if (activeFilterTag === 'all') return typeFilteredTiles;
    return typeFilteredTiles.filter((tile) => normalizeTileTags(tile.tags).includes(activeFilterTag));
  }, [activeFilterTag, typeFilteredTiles]);

  useEffect(() => {
    if (!filteredTiles.length) {
      if (selectedTileId !== NEW_TILE_ID) {
        setSelectedTileId(null);
        setDraft(null);
      }
      return;
    }

    if (selectedTileId === NEW_TILE_ID) {
      return;
    }

    if (selectedTileId && selectedTileId !== NEW_TILE_ID && filteredTiles.some((tile) => tile.id === selectedTileId)) {
      return;
    }

    const first = filteredTiles[0];
    setSelectedTileId(first.id);
    setDraft(createDraftFromTile(first, first.type));
  }, [filteredTiles, selectedTileId]);

  const handleSelectTile = (tile) => {
    setErrorMessage('');
    setSelectedTileId(tile.id);
    setDraft(createDraftFromTile(tile, tile.type));
  };

  const handleNewTile = () => {
    const defaultType = filterType === TILE_KIND_DETAIL ? TILE_KIND_DETAIL : TILE_KIND_BASE;
    const nextDraft = createDraftFromTile(null, defaultType);
    if (activeFilterTag !== 'all') {
      nextDraft.tags = activeFilterTag;
    }
    setErrorMessage('');
    setSelectedTileId(NEW_TILE_ID);
    setDraft(nextDraft);
  };

  const handleFieldChange = (field, value) => {
    setCopyMessage('');
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleTypeChange = (value) => {
    setCopyMessage('');
    const nextType = value === TILE_KIND_DETAIL ? TILE_KIND_DETAIL : TILE_KIND_BASE;
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        type: nextType,
        walkable: nextType === TILE_KIND_BASE ? prev.walkable : true,
        renderScaleTiles: nextType === TILE_KIND_DETAIL
          ? normalizeRenderScaleTiles(prev.renderScaleTiles)
          : DEFAULT_RENDER_SCALE_TILES,
        renderWidthTiles: nextType === TILE_KIND_DETAIL
          ? normalizeRenderDimensionTiles(
            prev.renderWidthTiles,
            prev.renderScaleTiles,
          )
          : DEFAULT_RENDER_SCALE_TILES,
        renderHeightTiles: nextType === TILE_KIND_DETAIL
          ? normalizeRenderDimensionTiles(
            prev.renderHeightTiles,
            prev.renderScaleTiles,
          )
          : DEFAULT_RENDER_SCALE_TILES,
      };
    });
  };

  const handleUpload = async (event) => {
    if (!event?.target?.files?.length) return;
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;

    const uploadedId = await AdminAPI.handleMediaUpload(file, 'progresslands.tile.v1');
    if (!uploadedId) return;

    setDraft((prev) => (prev ? { ...prev, imageId: uploadedId } : prev));
  };

  const handleRemoveImage = () => {
    setDraft((prev) => (prev ? { ...prev, imageId: null } : prev));
  };

  const handleSave = async () => {
    const validationError = validateTileDraft(draft);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    if (!draft) return;

    const payload = {
      name: draft.name.trim(),
      key: draft.key.trim(),
      type: draft.type,
      category: draft.category?.trim() || '',
      tags: normalizeTileTags(draft.tags),
      visualDescription: draft.visualDescription?.trim() || '',
      fallbackColor: draft.fallbackColor || (draft.type === TILE_KIND_DETAIL ? '#94a3b8' : '#6b7280'),
      imageId: draft.imageId || null,
      walkable: draft.type === TILE_KIND_BASE ? !!draft.walkable : true,
      renderScaleTiles: draft.type === TILE_KIND_DETAIL
        ? Math.min(
          MAX_RENDER_SCALE_TILES,
          Math.max(
            Number(draft.renderWidthTiles),
            Number(draft.renderHeightTiles),
          ),
        )
        : DEFAULT_RENDER_SCALE_TILES,
      renderWidthTiles: draft.type === TILE_KIND_DETAIL
        ? Number(draft.renderWidthTiles)
        : null,
      renderHeightTiles: draft.type === TILE_KIND_DETAIL
        ? Number(draft.renderHeightTiles)
        : null,
    };

    setIsSaving(true);
    setErrorMessage('');

    try {
      if (draft.id) {
        const ok = await updateTile(draft.id, payload);
        if (!ok) {
          setErrorMessage('Failed to update tile');
          return;
        }
        await loadTiles(draft.id);
      } else {
        const created = await createTile(payload);
        if (!created) {
          setErrorMessage('Failed to create tile');
          return;
        }
        await loadTiles(created.id || created.Id);
      }
    } catch (error) {
      console.error('Failed to save tile', error);
      setErrorMessage('Failed to save tile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft?.id) return;
    setIsSaving(true);
    setErrorMessage('');
    try {
      const ok = await deleteTile(draft.id);
      if (!ok) {
        setErrorMessage('Failed to delete tile');
        return;
      }
      await loadTiles();
    } catch (error) {
      console.error('Failed to delete tile', error);
      setErrorMessage('Failed to delete tile');
    } finally {
      setIsSaving(false);
    }
  };

  const tileImagePrompt = draft ? buildTileImagePrompt(draft) : '';

  const handleCopyPrompt = async () => {
    setCopyMessage('');
    try {
      await navigator.clipboard.writeText(tileImagePrompt);
      setCopyMessage('Prompt copied.');
    } catch (error) {
      console.error('Failed to copy tile image prompt', error);
      setCopyMessage('Copy failed. Select the prompt and copy it manually.');
    }
  };

  const previewVariant = draft?.type === TILE_KIND_DETAIL
    ? MEDIA_VARIANTS.TILE_DETAIL
    : MEDIA_VARIANTS.MODAL;
  const previewUrl = draft?.imageId ? getImageUrl(draft.imageId, previewVariant) : null;

  return (
    <div className="admin-tiles">
      <div className="admin-tiles__sidebar">
        <div className="admin-tiles__header">
          <div className="admin-tiles__filters">
            <button
              type="button"
              className={filterType === 'all' ? 'is-active' : ''}
              onClick={() => setFilterType('all')}
              aria-pressed={filterType === 'all'}
            >
              All
            </button>
            <button
              type="button"
              className={filterType === TILE_KIND_BASE ? 'is-active' : ''}
              onClick={() => setFilterType(TILE_KIND_BASE)}
              aria-pressed={filterType === TILE_KIND_BASE}
            >
              Base Tiles
            </button>
            <button
              type="button"
              className={filterType === TILE_KIND_DETAIL ? 'is-active' : ''}
              onClick={() => setFilterType(TILE_KIND_DETAIL)}
              aria-pressed={filterType === TILE_KIND_DETAIL}
            >
              Details
            </button>
          </div>
          <label className="admin-tiles__tag-filter">
            <span>Tag</span>
            <select
              value={activeFilterTag}
              onChange={(event) => setFilterTag(event.target.value)}
              aria-label="Filter tiles by tag"
            >
              <option value="all">All tags</option>
              {tagOptions.map(({ tag, count }) => (
                <option key={tag} value={tag}>#{tag} ({count})</option>
              ))}
            </select>
          </label>
          <button type="button" className="button-primary" onClick={handleNewTile}>
            New Tile
          </button>
        </div>
        <ul className="admin-tiles__list">
          {!filteredTiles.length && (
            <li className="admin-tiles__list-empty">No tiles match the selected filters.</li>
          )}
          {filteredTiles.map((tile) => {
            const isActive = selectedTileId === tile.id;
            const imageVariant = tile.type === TILE_KIND_DETAIL
              ? MEDIA_VARIANTS.TILE_DETAIL
              : MEDIA_VARIANTS.TILE;
            const imageUrl = tile.imageId ? getImageUrl(tile.imageId, imageVariant) : null;
            const swatchStyle = {
              backgroundColor: tile.fallbackColor || '#6b7280',
              backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
            };
            return (
              <li key={tile.id}>
                <button
                  type="button"
                  className={`admin-tiles__tile ${isActive ? 'is-active' : ''}`}
                  onClick={() => handleSelectTile(tile)}
                  aria-pressed={isActive}
                >
                  <span
                    className={`admin-tiles__swatch ${imageUrl ? 'has-image' : ''}`}
                    style={swatchStyle}
                    aria-hidden="true"
                  />
                  <span className="admin-tiles__meta">
                    <strong className="admin-tiles__title">
                      <AdminColorDot item={tile} />
                      {tile.label}
                    </strong>
                    <span>{tile.type === TILE_KIND_BASE ? 'Base Tile' : 'Detail'}{tile.category ? ` · ${tile.category}` : ''}</span>
                    {!!tile.tags.length && (
                      <span className="admin-tiles__tags">
                        {tile.tags.map((tag) => <span key={tag}>#{tag}</span>)}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="admin-tiles__detail">
        {!draft && <div className="admin-tiles__empty">Select a tile or create a new one to edit its details.</div>}
        {draft && (
          <div className="admin-tiles__form">
            <h3>{draft.id ? 'Edit Tile' : 'New Tile'}</h3>
            {errorMessage && <div className="admin-tiles__error" role="alert">{errorMessage}</div>}
            <label className="form-control">
              <span>Name</span>
              <input type="text" value={draft.name} onChange={(e) => handleFieldChange('name', e.target.value)} />
            </label>
            <label className="form-control">
              <span>Key</span>
              <input type="text" value={draft.key} onChange={(e) => handleFieldChange('key', e.target.value)} />
            </label>
            <label className="form-control">
              <span>Type</span>
              <select value={draft.type} onChange={(e) => handleTypeChange(e.target.value)}>
                <option value={TILE_KIND_BASE}>Base Tile</option>
                <option value={TILE_KIND_DETAIL}>Detail</option>
              </select>
            </label>
            {draft.type === TILE_KIND_DETAIL && (
              <>
                <label className="form-control">
                  <span>Render width (tiles)</span>
                  <input
                    type="number"
                    min={MIN_RENDER_SCALE_TILES}
                    max={MAX_RENDER_DIMENSION_TILES}
                    step="0.25"
                    value={draft.renderWidthTiles}
                    onChange={(e) => handleFieldChange('renderWidthTiles', e.target.value)}
                  />
                </label>
                <label className="form-control">
                  <span>Render height (tiles)</span>
                  <input
                    type="number"
                    min={MIN_RENDER_SCALE_TILES}
                    max={MAX_RENDER_DIMENSION_TILES}
                    step="0.25"
                    value={draft.renderHeightTiles}
                    onChange={(e) => handleFieldChange('renderHeightTiles', e.target.value)}
                  />
                </label>
              </>
            )}
            <label className="form-control">
              <span>Category</span>
              <input type="text" value={draft.category} onChange={(e) => handleFieldChange('category', e.target.value)} />
            </label>
            <label className="form-control">
              <span>Tags</span>
              <input
                type="text"
                value={draft.tags}
                onChange={(e) => handleFieldChange('tags', e.target.value)}
                placeholder="#desert, #beach, #amnesiacove"
                aria-label="Tags"
                aria-describedby="admin-tiles-tags-hint"
              />
              <small id="admin-tiles-tags-hint" className="admin-tiles__field-hint">
                Separate tags with commas. A leading # is optional.
              </small>
            </label>
            <label className="form-control">
              <span>Visual Description</span>
              <textarea
                rows="4"
                value={draft.visualDescription}
                onChange={(e) => handleFieldChange('visualDescription', e.target.value)}
                placeholder="Describe the terrain, materials, colors, wear, and distinctive details."
              />
            </label>
            {draft.type === TILE_KIND_BASE && (
              <label className="form-control form-control--inline">
                <input
                  type="checkbox"
                  checked={!!draft.walkable}
                  onChange={(e) => handleFieldChange('walkable', e.target.checked)}
                />
                <span>Walkable</span>
              </label>
            )}
            <label className="form-control">
              <span>Fallback Color</span>
              <div className="admin-tiles__color-input">
                <input
                  type="color"
                  value={draft.fallbackColor || '#6b7280'}
                  onChange={(e) => handleFieldChange('fallbackColor', e.target.value)}
                />
                <input
                  type="text"
                  value={draft.fallbackColor || ''}
                  onChange={(e) => handleFieldChange('fallbackColor', e.target.value)}
                />
              </div>
            </label>
            <div className="form-control">
              <span>Image</span>
              <div className="admin-tiles__image-preview">
                {previewUrl ? (
                  <img src={previewUrl} alt={draft.name || 'Tile'} />
                ) : (
                  <div
                    className="admin-tiles__image-placeholder"
                    style={{ backgroundColor: draft.fallbackColor || '#6b7280' }}
                  />
                )}
              </div>
              <div className="admin-tiles__image-actions">
                <label className="button-secondary">
                  Upload
                  <input type="file" accept="image/*" onChange={handleUpload} hidden />
                </label>
                {draft.imageId && (
                  <button type="button" className="button-secondary" onClick={handleRemoveImage}>
                    Remove Image
                  </button>
                )}
              </div>
            </div>
            <details className="form-control admin-tiles__prompt admin-image-prompt">
              <summary>Image Generator Prompt</summary>
              <textarea value={tileImagePrompt} rows="18" readOnly aria-label="Image Generator Prompt" />
              <div className="admin-tiles__prompt-actions">
                <button type="button" className="button-secondary" onClick={handleCopyPrompt}>
                  Copy Prompt
                </button>
                {copyMessage && <span role="status">{copyMessage}</span>}
              </div>
            </details>
            <div className="admin-tiles__actions">
              <button
                type="button"
                className="button-primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving…' : 'Save Tile'}
              </button>
              {draft.id && (
                <button
                  type="button"
                  className="button-secondary"
                  onClick={handleDelete}
                  disabled={isSaving}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminTiles;
